import React from 'react';
import { useIntl } from 'react-intl';
import { ModalBody, ModalFooter } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { StudioPublishSwitch } from '../publish/StudioPublishSwitch';
import { addedErrorText } from './addedErrors';
import { addedAssetsMessages as messages } from './addedAssetsMessages';
import { AddAssetRequest, closeAddAssetDialog } from './addAssetDialogStore';
import { isAddedUploadRunning } from './addedUploadStore';
import { useAddedUploadTab } from './useAddedUploadTab';
import { UploadDropZone } from './UploadDropZone';
import { UploadProgressCard } from './UploadProgressCard';
import styles from './UploadTab.module.scss';

interface UploadTabProps {
  request: AddAssetRequest;
  tabs: React.ReactNode;
}

/**
 * "Upload the file" (design a4): the browser sends the file straight to S3 in parts (D5), so a big
 * file has no limit of ours and the user can close the dialog while it goes up. A refusal of the
 * rules (type, size, the Canvas length and shape) shows its sentence and the pick comes back.
 */
const UploadTabComponent: React.FC<UploadTabProps> = ({ request, tabs }) => {
  const intl = useIntl();
  const { upload, pickFile, stop, reset, offersPublish, choice, changeChoice } = useAddedUploadTab(request);
  const isRunning = isAddedUploadRunning(upload);
  const isDone = upload?.phase === 'done';
  const failed = upload?.phase === 'failed' ? upload.error : null;

  const close = () => {
    if (!isRunning) {
      reset();
    }
    closeAddAssetDialog();
  };

  return (
    <>
      <ModalBody className={styles.body}>
        {tabs}
        {upload === null || failed !== null ? (
          <UploadDropZone themeId={request.themeId} onFile={pickFile} />
        ) : (
          <UploadProgressCard upload={upload} songTitle={request.songTitle} songDurationSec={request.songDurationSec} />
        )}
        {failed !== null && (
          <span className={styles.error} role="alert">
            {addedErrorText(intl, failed, request.themeId)}
          </span>
        )}
        {offersPublish && !isDone && (
          <StudioPublishSwitch
            accountId={request.accountId}
            releaseDate={request.releaseDate}
            value={choice}
            onChange={changeChoice}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <span className={styles.hint}>
          {intl.formatMessage(request.themeId === 'canvasLoop' ? messages.footCanvas : messages.footLong)}
        </span>
        {isRunning && upload?.phase === 'uploading' && (
          <button type="button" className={styles.link} onClick={stop}>
            {intl.formatMessage(messages.cancelUpload)}
          </button>
        )}
        <SMLButton onClick={close}>{intl.formatMessage(isDone ? messages.done : messages.close)}</SMLButton>
        {isRunning && (
          <SMLButton variant="primary" disabled>
            {intl.formatMessage(upload?.phase === 'checking' ? messages.checking : messages.uploading)}
          </SMLButton>
        )}
      </ModalFooter>
    </>
  );
};

export const UploadTab = TypedMemo(UploadTabComponent);
